// Shared show_if evaluation — mirrors backend/services/form_logic.py so the
// builder, the form filler and the PDF agree on what "visible" means.

// Categories that support per-instance section hide/show + on-the-fly
// renumbering (see visibleSections below). Every other template renders its
// section titles exactly as stored.
export const FIRE_CHECK_CATEGORIES = new Set(["fire_check", "fire_check_monthly"]);

const NUM_PREFIX_RE = /^\s*\d+(?:\.\d+)*[a-z]?(?:\s*[-–.]\s*|\s+)/;

const PROTECTED_FIRE_CHECK_SECTION_TITLES = new Set(["inspection details", "completion and sign-off"]);

export function stripNumberPrefix(title) {
    return (title || "").replace(NUM_PREFIX_RE, "").trim();
}

// Mirrors backend/services/form_logic.py::is_section_hideable — prefers the
// explicit hideable flag but falls back to a title match, so protected
// sections stay protected even on a stale pre-migration template_snapshot.
export function isSectionHideable(sec) {
    if (sec?.hideable === false) return false;
    return !PROTECTED_FIRE_CHECK_SECTION_TITLES.has(stripNumberPrefix(sec?.title).toLowerCase());
}

export function hiddenSectionIds(instance) {
    return new Set((instance?.hidden_sections || []).map((h) => h.section_id).filter(Boolean));
}

export function sectionHasAnyVisibleField(sec, answers) {
    return (sec?.fields || []).some(
        (f) => evaluateShowIf(f.show_if, answers) && !(f.field_type === "instruction" && f.internal_only)
    );
}

// Mirrors backend/services/form_logic.py::visible_sections exactly — single
// source of truth for which sections render in the exported PDF/Word (and,
// for fire_check/fire_check_monthly templates only, their display_title with
// stripped + renumbered 01, 02, 03... titles). Other templates get
// display_title === title, unchanged.
export function visibleSections(template, instance, answers) {
    const hiddenIds = hiddenSectionIds(instance);
    const isFireCheck = FIRE_CHECK_CATEGORIES.has(template?.category);
    const visible = (template?.sections || []).filter(
        (s) => s.enabled !== false && evaluateShowIf(s.show_if, answers) && !hiddenIds.has(s.id)
            && (!isFireCheck || sectionHasAnyVisibleField(s, answers))
    );
    return visible.map((s, i) => ({
        ...s,
        display_title: isFireCheck ? `${String(i + 1).padStart(2, "0")} - ${stripNumberPrefix(s.title)}` : s.title,
    }));
}

function isEmpty(v) {
    return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
}

export function evaluateShowIf(showIf, answers) {
    if (!showIf) return true;
    const conds = showIf.all || [];
    if (!conds.length) return true;
    const a = answers || {};
    return conds.every((c) => {
        const actual = a[c.field_id];
        const expected = c.value;
        const op = c.op || "eq";
        if (op === "not_empty") return !isEmpty(actual);
        if (op === "empty") return isEmpty(actual);
        if (op === "neq") {
            if (Array.isArray(actual)) return !actual.map(String).includes(String(expected));
            return String(actual ?? "") !== String(expected ?? "");
        }
        // eq (default)
        if (Array.isArray(actual)) return actual.map(String).includes(String(expected));
        return String(actual ?? "") === String(expected ?? "");
    });
}

function hasAnswerData(field, answers) {
    const v = (answers || {})[field.id];
    if (Array.isArray(v)) return v.length > 0;
    return v !== undefined && v !== null && v !== "";
}

// Flattens every show_if-bearing section/field/grid-row in the template so a
// value change can be checked against each one.
function dependentsReferencing(template, fieldId) {
    const deps = [];
    for (const sec of (template?.sections || [])) {
        if (sec.show_if && (sec.show_if.all || []).some((c) => c.field_id === fieldId)) {
            deps.push({ kind: "section", show_if: sec.show_if, section: sec });
        }
        for (const f of (sec.fields || [])) {
            if (f.show_if && (f.show_if.all || []).some((c) => c.field_id === fieldId)) {
                deps.push({ kind: "field", show_if: f.show_if, field: f });
            }
            if (f.field_type === "check_grid") {
                for (const row of ((f.grid || {}).rows || [])) {
                    if (row.show_if && (row.show_if.all || []).some((c) => c.field_id === fieldId)) {
                        deps.push({ kind: "row", show_if: row.show_if, field: f, row });
                    }
                }
            }
        }
    }
    return deps;
}

// Returns a list of human-readable labels for anything that is currently
// visible + has real answered data, but would become hidden if `fieldId`
// were set to `newVal`. Used to confirm before silently excluding data.
export function computeNewlyHiddenAnswered(template, oldAnswers, fieldId, newVal) {
    const newAnswers = { ...(oldAnswers || {}), [fieldId]: newVal };
    const deps = dependentsReferencing(template, fieldId);
    const hits = [];
    for (const d of deps) {
        const wasVisible = evaluateShowIf(d.show_if, oldAnswers);
        const nowVisible = evaluateShowIf(d.show_if, newAnswers);
        if (!wasVisible || nowVisible) continue;
        if (d.kind === "section") {
            const hasData = (d.section.fields || []).some((f) => hasAnswerData(f, oldAnswers));
            if (hasData) hits.push(d.section.title || "a section");
        } else if (d.kind === "field") {
            if (hasAnswerData(d.field, oldAnswers)) hits.push(d.field.label || "a field");
        } else if (d.kind === "row") {
            const rowAns = ((oldAnswers || {})[d.field.id] || {})[d.row.id];
            if (rowAns && Object.keys(rowAns).length > 0) {
                const idCols = (d.field.grid || {}).id_columns || [];
                const rowLabel = idCols.map((c) => d.row.id_values?.[c.id]).filter(Boolean).join(" / ");
                hits.push(`${d.field.label || "grid"}${rowLabel ? ` — ${rowLabel}` : ""}`);
            }
        }
    }
    return hits;
}

// Single source of truth for which check_grid rows are "live" on an instance:
// template rows (minus any instance-removed `_removed_rows`, with per-instance
// `_row_overrides` applied on top of their id_values) plus this instance's own
// freely-added `_extra_rows`. Mirrors backend/services/form_logic.py::effective_grid_rows.
export function effectiveGridRows(grid, gridAnswers, answers) {
    const ga = gridAnswers || {};
    const removed = new Set(ga._removed_rows || []);
    const overrides = ga._row_overrides || {};
    const configured = (grid?.rows || [])
        .filter((r) => !r.retired && !removed.has(r.id) && evaluateShowIf(r.show_if, answers))
        .map((r) => (overrides[r.id] ? { ...r, id_values: { ...r.id_values, ...overrides[r.id] } } : r));
    const extra = (ga._extra_rows || []).filter((r) => evaluateShowIf(r.show_if, answers));
    return [...configured, ...extra];
}


// Mirrors backend/services/form_logic.py::find_findings_field /
// compute_key_findings — the BRPD v3 "Key findings" auto-table.
export function findFindingsField(template) {
    for (const sec of template?.sections || []) {
        for (const fld of sec.fields || []) {
            if (fld.field_type === "finding_repeater") return fld;
        }
    }
    return null;
}

export function computeKeyFindings(template, answers) {
    const fld = findFindingsField(template);
    if (!fld) return [];
    const rows = (answers || {})[fld.id] || [];
    return rows.map((r, i) => ({
        num: `F${i + 1}`,
        title: r?.title || "",
        requirement: r?.requirement || "",
        action: r?.action || "",
        status: r?.status || "",
    }));
}
